import { NavLink, useLocation, useParams } from "react-router-dom";
import { WorkspaceShell } from "@/shell/WorkspaceShell";
import { cn } from "@/design/cn";
import { useAuditEvents, useActiveProfile } from "@/domain/hooks";
import { SpendingAuthority } from "@/screens/authority/SpendingAuthority";
import { ConnectionsPage } from "@/screens/connections/ConnectionsPage";
import { WorkspaceTeamPage } from "./WorkspacePages";
import { AuditTrail } from "@/screens/audit/AuditTrail";

/** Route-driven tab bar for a workspace hub (IA redesign). */
function WorkspaceTabBar({ tabs }: { tabs: { label: string; to: string; end?: boolean }[] }) {
  return (
    <div className="mb-5 flex flex-wrap gap-1 border-b border-line">
      {tabs.map((t) => (
        <NavLink
          key={t.to}
          to={t.to}
          end={t.end}
          className={({ isActive }) =>
            cn(
              "relative -mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              isActive ? "border-brand text-ink" : "border-transparent text-mute hover:text-ink",
            )
          }
        >
          {t.label}
        </NavLink>
      ))}
    </div>
  );
}

function HubPage({ title, subtitle, tabs, children }: { title: string; subtitle: string; tabs: { label: string; to: string; end?: boolean }[]; children: React.ReactNode }) {
  return (
    <WorkspaceShell>
      <div className="px-4 py-5 md:px-8 md:py-6">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
        <p className="mt-1 text-sm text-mute">{subtitle}</p>
        <div className="mt-6">
          <WorkspaceTabBar tabs={tabs} />
          {children}
        </div>
      </div>
    </WorkspaceShell>
  );
}

/** Finance & controls hub — the money setup/controls that don't each merit a sidebar slot. */
export function FinanceControlsPage() {
  const { workspaceId } = useParams();
  const { pathname } = useLocation();
  const base = `/workspace/${workspaceId}/finance`;
  const onConnections = pathname.endsWith("/connections");
  return (
    <HubPage
      title="Finance & controls"
      subtitle="Delegated spending authority and the connected money sources that feed the ledger."
      tabs={[
        { label: "Spending authority", to: base, end: true },
        { label: "Connections", to: `${base}/connections` },
      ]}
    >
      {onConnections ? <ConnectionsPage embedded /> : <SpendingAuthority embedded />}
    </HubPage>
  );
}

/** Settings hub — reference surfaces (participants, audit) grouped out of daily navigation. */
export function WorkspaceSettingsPage() {
  const { workspaceId } = useParams();
  const { pathname } = useLocation();
  const profile = useActiveProfile();
  const auditEvents = useAuditEvents();
  const base = `/workspace/${workspaceId}/settings`;
  const onAudit = pathname.endsWith("/audit");
  // Audit is hidden for contractors (matches prior behaviour).
  const isContractor = profile.group === "contractor";
  const tabs = isContractor
    ? [{ label: "Participants", to: base, end: true }]
    : [{ label: "Participants", to: base, end: true }, { label: "Audit trail", to: `${base}/audit` }];
  return (
    <HubPage title="Settings" subtitle="Participants in this programme and the workspace audit log." tabs={tabs}>
      {onAudit && !isContractor ? <AuditTrail events={auditEvents} /> : <WorkspaceTeamPage embedded />}
    </HubPage>
  );
}
